import { FieldGroup, Field, Label, Select } from '../../../atoms'
import React, { useEffect } from 'react'
import { words, capitalize } from 'lodash-es'
import { countries } from './country-iso-codes'
import dayjs from '../../../utils/date'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
dayjs.extend(isSameOrAfter)
export interface DirectorDetails {
  name?: string
  title?: string
  firstName?: string
  lastName?: string
  dateOfBirth?: string
  dateOfBirthFields?: {
    day?: string
    month?: string
    year?: string
  }
  emailAddress?: string
  mobileNumber?: string
  workNumber?: string
}

const getNames = (name: string) => {
  const [, lastName, firstName] = name.match(/([^,]*), (\S*)/) || []
  return [firstName, capitalize(lastName)]
}

// formatName('SYED, Tariq Sajjad') => 'Tariq Syed'
const formatName = (name: string) => getNames(name).join(' ')

const getFirstName = (directorDetails: DirectorDetails) => getNames(directorDetails.name ?? '')[0]
const getLastName = (directorDetails: DirectorDetails) => getNames(directorDetails.name ?? '')[1]

const toLabel = (field: string) => words(field).map(capitalize).join(' ')

const isOver18 = (birthDate: Date) => {
  const today = dayjs()
  const age18 = dayjs(birthDate).add(18, 'year')
  return today.isSameOrAfter(age18)
}

const DirectorFinder = ({
  directorDetails,
  businessDirectors,
  register,
  registerWithTrack,
  setValue,
  errors,
  setError,
  clearErrors,
  over18Check = false,
}) => {
  const DirectorFields = {
    title: {
      validation: {
        required: { value: true, message: 'Please enter your title' },
        maxLength: {
          value: 50,
          message: 'Maximum 50 character title',
        },
      },
    },
    firstName: {
      validation: {
        required: { value: true, message: 'Please enter your first name' },
        maxLength: {
          value: 50,
          message: 'Maximum 50 character first name',
        },
        pattern: {
          value: /^[a-zA-Z]([a-zA-Z0-9 '-]*)$/,
          message: 'Please enter a valid first name',
        },
      },
    },
    lastName: {
      validation: {
        required: { value: true, message: 'Please enter your last name' },
        maxLength: {
          value: 50,
          message: 'Maximum 50 character last name',
        },
        pattern: {
          value: /^[a-zA-Z]([a-zA-Z0-9 '-]*)$/,
          message: 'Please enter a valid last name',
        },
      },
    },
    dateOfBirth: {
      validation: {
        day: {
          required: {
            value: true,
            message: 'Please enter your day of birth',
          },
          min: {
            value: 1,
            message: 'Day must be between 1 and 31',
          },
          max: {
            value: 31,
            message: 'Day must be between 1 and 31',
          },
          pattern: {
            value: /^\d+$/,
            message: 'Day must be a valid number',
          },
        },
        month: {
          required: 'Please enter the month of your birth',
          min: {
            value: 1,
            message: 'Month must be between 1 and 12',
          },
          max: {
            value: 12,
            message: 'Month must be between 1 and 12',
          },
          pattern: {
            value: /^\d+$/,
            message: 'Month must be a valid number',
          },
        },
        year: {
          required: 'Please enter the year of your birth',
          pattern: {
            value: /^(19|20)\d{2}$/,
            message: 'Year must be a valid 4-digit number starting with 19 or 20',
          },
        },
      },
    },
    mobileNumber: {
      validation: {
        required: { value: true, message: 'Please enter your mobile number' },
        pattern: {
          value: /^07[0-9]{9}$/,
          message: 'Please enter a valid mobile number beginning with 07',
        },
      },
    },
    nationality: {
      validation: {
        required: { value: true, message: 'Please enter your Nationality' },
      },
    },
  }
  const fields = Object.keys(DirectorFields)

  useEffect(() => {
    if (
      !errors?.directorDetails?.dateOfBirthFields &&
      over18Check &&
      directorDetails?.dateOfBirthFields?.year
    ) {
      const birthDate = new Date(
        directorDetails?.dateOfBirthFields?.year,
        directorDetails?.dateOfBirthFields?.month - 1,
        directorDetails?.dateOfBirthFields?.day,
      )
      if (isOver18(birthDate)) {
        clearErrors('directorDetails.underAge')
      } else {
        setError('directorDetails.underAge', { message: 'You must be older than 18 to apply' })
      }
    }
  }, [
    directorDetails?.dateOfBirthFields?.day,
    directorDetails?.dateOfBirthFields?.month,
    directorDetails?.dateOfBirthFields?.year,
    errors?.directorDetails?.dateOfBirthFields,
  ])

  return (
    <Field className='SMEform__companyDetails'>
      <div className='SMEform__directorDetails__detailsBox'>
        <div className='SMEform__title__box'>
          <Label className='SMEform__title'>
            {businessDirectors?.length > 0 ? 'Director details' : 'Personal Details'}
          </Label>
        </div>
        <div className='SMEform__input'>
          {businessDirectors?.length > 0 && (
            <Field>
              <Select
                name='director-details'
                value={directorDetails.name ?? 'default'}
                onChange={(e) => {
                  const rawDirectorDetails = businessDirectors.find(
                    (d) => d.name === e.target.value,
                  )

                  if (rawDirectorDetails) {
                    Object.keys(rawDirectorDetails).forEach((k) => {
                      register(`directorDetails.${k}`, rawDirectorDetails[k])
                    })
                    register(`directorDetails.lastName`, getFirstName(rawDirectorDetails))
                    register(`directorDetails.firstName`, getLastName(rawDirectorDetails))

                    setValue('directorDetails', {
                      ...rawDirectorDetails,
                      firstName: getFirstName(rawDirectorDetails),
                      lastName: getLastName(rawDirectorDetails),
                    })
                  }
                }}
                options={[
                  { value: 'default', text: 'Please select' },
                  ...businessDirectors.map(({ name }: { name: string }) => {
                    return {
                      value: name,
                      text: formatName(name),
                    }
                  }),
                ]}
              />
            </Field>
          )}
          {(businessDirectors?.length < 1 || directorDetails.firstName) && (
            <>
              {fields.map((field, index) => {
                return field === 'title' ? (
                  <Field key={index}>
                    <Label className='required'>{toLabel(field)}</Label>
                    <Select
                      name={`directorDetails.${field}`}
                      value={directorDetails[field] ?? ''}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setValue(`directorDetails.${field}`, e.target.value)
                      }}
                      options={[
                        { value: '', text: 'Please select' },
                        { value: 'Mr', text: 'Mr' },
                        { value: 'Mrs', text: 'Mrs' },
                        { value: 'Miss', text: 'Miss' },
                        { value: 'Ms', text: 'Ms' },
                        { value: 'Dr', text: 'Dr' },
                      ]}
                      ref={registerWithTrack(DirectorFields[field].validation, field)}
                    />
                    {errors?.directorDetails?.title && (
                      <div className='SMEform__error'>{errors?.directorDetails?.title.message}</div>
                    )}
                  </Field>
                ) : field === 'dateOfBirth' ? (
                  <Field key={index}>
                    <Label className='required'>Date of Birth</Label>
                    <FieldGroup>
                      <input
                        placeholder='DD'
                        value={directorDetails?.dateOfBirthFields?.day ?? ''}
                        name='directorDetails.dateOfBirthFields.day'
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          setValue(`directorDetails.dateOfBirthFields.day`, e.target.value)
                        }}
                        ref={registerWithTrack(
                          DirectorFields[field].validation.day,
                          'dateOfBirthFields.day',
                        )}
                      />
                      <input
                        placeholder='MM'
                        value={directorDetails?.dateOfBirthFields?.month ?? ''}
                        name='directorDetails.dateOfBirthFields.month'
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          setValue(`directorDetails.dateOfBirthFields.month`, e.target.value)
                        }}
                        ref={registerWithTrack(
                          DirectorFields[field].validation.month,
                          'dateOfBirthFields.month',
                        )}
                      />
                      <input
                        placeholder='YYYY'
                        value={directorDetails?.dateOfBirthFields?.year ?? ''}
                        name='directorDetails.dateOfBirthFields.year'
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          setValue(`directorDetails.dateOfBirthFields.year`, e.target.value)
                        }}
                        ref={registerWithTrack(
                          DirectorFields[field].validation.year,
                          'dateOfBirthFields.year',
                        )}
                      />
                    </FieldGroup>
                    {errors?.directorDetails?.dateOfBirthFields && (
                      <div className='SMEform__error'>
                        {['day', 'month', 'year']
                          .map((f) => errors?.directorDetails?.dateOfBirthFields[f]?.message)
                          .find((f) => f)}
                      </div>
                    )}
                    {errors?.directorDetails?.underAge && (
                      <div className='SMEform__error'>
                        {errors?.directorDetails?.underAge.message}
                      </div>
                    )}
                  </Field>
                ) : field === 'nationality' ? (
                  <Field key={index}>
                    <Label className='required'>{toLabel(field)}</Label>
                    <Select
                      name={`directorDetails.${field}`}
                      value={directorDetails[field] ?? ''}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setValue(`directorDetails.${field}`, e.target.value)
                      }}
                      options={[{ value: '', text: 'Please select' }].concat(
                        countries.map((n: any) => {
                          return { value: n.value, text: n.nationality }
                        }),
                      )}
                      ref={registerWithTrack(DirectorFields[field].validation, field)}
                    />
                    {errors?.directorDetails?.[field] && (
                      <div className='SMEform__error'>
                        {errors?.directorDetails?.[field].message}
                      </div>
                    )}
                  </Field>
                ) : (
                  <Field key={index}>
                    <Label className='required'>{toLabel(field)}</Label>
                    <input
                      name={`directorDetails.${field}`}
                      value={directorDetails[field] ?? ''}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setValue(`directorDetails.${field}`, e.target.value)
                      }}
                      type={'text'}
                      ref={registerWithTrack(DirectorFields[field].validation, field)}
                    />
                    {errors?.directorDetails?.[field] && (
                      <div className='SMEform__error'>
                        {errors?.directorDetails?.[field].message}
                      </div>
                    )}
                  </Field>
                )
              })}
            </>
          )}
        </div>
      </div>
    </Field>
  )
}

export default DirectorFinder
